import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import { ErrorFrontend } from '@core/errors';
import { PaginatedResponse, PaginationParams, Upload, UploadListItem } from '@core/models';

/**
 * Upload Service — the orientador's pre-approval surface.
 *
 * An Upload is one scanned file; its Page cards are the fórmula pages the
 * reader found inside it. Prescriptions don't exist until the order is
 * approved (the backend derives them from the pages), so everything the
 * orientador does pre-approval goes through here.
 */
@Injectable({
  providedIn: 'root',
})
export class UploadService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'intake/uploads';

  /**
   * Scan a file in: creates the Upload and starts the pipeline.
   * Backend expects: file, contract_id and (explicitly) the acting branch_id;
   * order_id attaches it to an existing order at birth.
   */
  async create(
    contractId: number,
    file: File,
    branchId: number,
    orderId: number | null = null,
  ): Promise<Upload> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('contract_id', contractId.toString());
    formData.append('branch_id', branchId.toString());
    if (orderId !== null) formData.append('order_id', orderId.toString());

    return await firstValueFrom(
      this.http.post<Upload>(`${this.baseUrl}/`, formData).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /** List uploads (containers with page cards). Removed ones are hidden. */
  async list(
    params?: PaginationParams & {
      order?: number;
      status?: string;
      search?: string;
      created_at__date?: string;
    },
  ): Promise<PaginatedResponse<UploadListItem>> {
    return await firstValueFrom(
      this.http
        .get<PaginatedResponse<UploadListItem>>(`${this.baseUrl}/`, {
          params: params as Record<string, string | number>,
        })
        .pipe(
          catchError((error: HttpErrorResponse) => {
            throw new ErrorFrontend(error);
          }),
        ),
    );
  }

  /**
   * Remove an upload (soft): the backend stamps who/when — never a status
   * overwrite — releases any stock its pages reserved, and a still-running
   * pipeline stops early on its own.
   */
  async remove(id: number): Promise<Upload> {
    return await firstValueFrom(
      this.http.post<Upload>(`${this.baseUrl}/${id}/remove/`, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /**
   * Retry a FAILED upload — the backend CONTINUES the run from its checkpoint
   * (completed stages, notably the expensive read, are not re-run). Only valid
   * for `failed` (technical); `rejected` is a real verdict → remove instead.
   */
  async retry(id: number): Promise<Upload> {
    return await firstValueFrom(
      this.http.post<Upload>(`${this.baseUrl}/${id}/retry/`, {}).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }

  /** Retry one FAILED page of the upload (the container's second retry level). */
  async retryPage(id: number, pageId: number): Promise<Upload> {
    return await firstValueFrom(
      this.http.post<Upload>(`${this.baseUrl}/${id}/retry_page/`, { page_id: pageId }).pipe(
        catchError((error: HttpErrorResponse) => {
          throw new ErrorFrontend(error);
        }),
      ),
    );
  }
}
